import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { 
  Users, Eye, ShoppingCart, TrendingUp, Clock, 
  CalendarIcon, ArrowUpRight, ArrowDownRight, Package
} from "lucide-react";
import { format, subDays } from "date-fns";
import { es } from "date-fns/locale";

const dateRanges = [
  { label: "Hoy", days: 0 },
  { label: "7 días", days: 7 },
  { label: "30 días", days: 30 },
  { label: "90 días", days: 90 },
];

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ days: 30, label: "30 días" });
  const [customRange, setCustomRange] = useState({ from: null, to: null });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, customRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      
      if (customRange.from && customRange.to) {
        startDate = customRange.from.toISOString();
        endDate = customRange.to.toISOString();
      } else {
        endDate = new Date().toISOString();
        startDate = subDays(new Date(), dateRange.days).toISOString();
      }
      
      const response = await axios.get(`${API}/admin/analytics`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Date Range Selector */}
      <div className="flex flex-wrap items-center gap-2">
        {dateRanges.map((range) => (
          <Button
            key={range.days}
            variant={dateRange.days === range.days && !customRange.from ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setDateRange(range);
              setCustomRange({ from: null, to: null });
            }}
          >
            {range.label}
          </Button>
        ))}
        
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="ml-2">
              <CalendarIcon className="h-4 w-4 mr-2" />
              {customRange.from && customRange.to 
                ? `${format(customRange.from, "dd MMM", { locale: es })} - ${format(customRange.to, "dd MMM", { locale: es })}`
                : "Personalizado"
              }
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={{ from: customRange.from, to: customRange.to }}
              onSelect={(range) => setCustomRange({ from: range?.from, to: range?.to })}
              numberOfMonths={2}
              locale={es}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Visitantes Únicos
            </CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.summary?.total_visitors || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Páginas Vistas
            </CardTitle>
            <Eye className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.summary?.total_pageviews || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Compras
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.summary?.total_purchases || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tasa Conversión
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.summary?.conversion_rate || 0}%</div>
            <p className="text-xs text-muted-foreground">Visitantes → Compras</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tiempo Medio
            </CardTitle>
            <Clock className="h-4 w-4 text-cyan-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDuration(analytics?.summary?.avg_session_duration || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Stats Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Visitantes y Compras por Día</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.daily_stats?.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span>Visitantes</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span>Compras</span>
                  </div>
                </div>
                
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {analytics.daily_stats.slice(-14).map((day, index) => {
                    const maxVisitors = Math.max(...analytics.daily_stats.map(d => d.visitors));
                    const visitorWidth = maxVisitors > 0 ? (day.visitors / maxVisitors * 100) : 0;
                    const purchaseWidth = maxVisitors > 0 ? (day.purchases / maxVisitors * 100) : 0;
                    
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-20 text-xs text-muted-foreground shrink-0">
                          {format(new Date(day.date), "dd MMM", { locale: es })}
                        </div>
                        <div className="flex-1 flex items-center gap-1">
                          <div 
                            className="h-4 bg-blue-500 rounded-sm transition-all"
                            style={{ width: `${visitorWidth}%`, minWidth: day.visitors > 0 ? '4px' : '0' }}
                          />
                          <span className="text-xs w-8">{day.visitors}</span>
                        </div>
                        <div className="flex-1 flex items-center gap-1">
                          <div 
                            className="h-4 bg-green-500 rounded-sm transition-all"
                            style={{ width: `${purchaseWidth}%`, minWidth: day.purchases > 0 ? '4px' : '0' }}
                          />
                          <span className="text-xs w-8">{day.purchases}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No hay datos para este período
              </p>
            )}
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Embudo de Conversión</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Visitors */}
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Visitantes</span>
                  <span className="text-sm font-bold">{analytics?.summary?.total_visitors || 0}</span>
                </div>
                <div className="h-8 bg-blue-500 rounded-lg w-full" />
              </div>
              
              {/* Page Views */}
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Páginas Vistas</span>
                  <span className="text-sm font-bold">{analytics?.summary?.total_pageviews || 0}</span>
                </div>
                <div 
                  className="h-8 bg-purple-500 rounded-lg"
                  style={{ 
                    width: analytics?.summary?.total_visitors > 0 
                      ? `${Math.min(100, (analytics.summary.total_pageviews / analytics.summary.total_visitors) * 20)}%`
                      : '0%'
                  }}
                />
              </div>
              
              {/* Purchases */}
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Compras</span>
                  <span className="text-sm font-bold">{analytics?.summary?.total_purchases || 0}</span>
                </div>
                <div 
                  className="h-8 bg-green-500 rounded-lg"
                  style={{ 
                    width: analytics?.summary?.total_visitors > 0 
                      ? `${Math.max(5, analytics.summary.conversion_rate)}%`
                      : '0%'
                  }}
                />
              </div>
              
              {/* Conversion Rate */}
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Tasa de Conversión</span>
                  <Badge className="bg-orange-500 text-white text-lg px-3 py-1">
                    {analytics?.summary?.conversion_rate || 0}%
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  De cada 100 visitantes, {analytics?.summary?.conversion_rate || 0} compran
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Páginas Más Visitadas</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.top_pages?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_pages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground w-6">
                        {index + 1}.
                      </span>
                      <span className="text-sm font-mono truncate max-w-[200px]">
                        {page.page}
                      </span>
                    </div>
                    <Badge variant="secondary">{page.views} visitas</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                No hay datos
              </p>
            )}
          </CardContent>
        </Card>

        {/* Top Packages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Productos Más Vistos</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.top_packages?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_packages.map((pkg, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate max-w-[200px]">
                        {pkg.name}
                      </span>
                    </div>
                    <Badge variant="secondary">{pkg.views} visitas</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                No hay datos de productos vistos
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
